import {
  logout,
  tokenStorage,
  refreshTokenStorage,
  userStorage,
  emailStorage,
  roleStorage,
} from '@/modules/infrastructure/users/services/auth.api';
import { displayUserName } from '@/modules/infrastructure/users/utils/displayUserName';
import { LogoutOutlined, SettingOutlined } from '@ant-design/icons';
import { history, useModel } from '@umijs/max';
import { Spin } from 'antd';
import type { MenuProps } from 'antd';
import { createStyles } from 'antd-style';
import React from 'react';
import { flushSync } from 'react-dom';
import { useIntl } from '@umijs/max';
import HeaderDropdown from '../HeaderDropdown';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};

  // Show appropriate name based on user state
  if (!currentUser) {
    return <span className="anticon">Loading...</span>;
  }

  if (currentUser.userid === 'unknown') {
    return <span className="anticon">Unknown User</span>;
  }

  if (currentUser.userid === 'id_required') {
    return <span className="anticon">{displayUserName(currentUser?.name)}</span>;
  }

  if (currentUser.userid === 'api_error') {
    return <span className="anticon">{displayUserName(currentUser?.name)}</span>;
  }

  return <span className="anticon">{displayUserName(currentUser?.name)}</span>;
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({ menu, children }) => {
  const intl = useIntl();

  /**
   * Logout and save the current url
   */
  const loginOut = async () => {
    try {
      // Try to notify the backend about logout
      // We skip the logic if it fails because we want to logout locally anyway
      await logout({ skipErrorHandler: true });
    } catch (error) {
      console.warn('Backend logout failed:', error);
    }

    // Always clear stored tokens and user data locally
    tokenStorage.remove();
    refreshTokenStorage.remove();
    userStorage.remove();
    emailStorage.remove();
    roleStorage.remove();

    const { search, pathname } = window.location;
    const urlParams = new URL(window.location.href).searchParams;
    const searchParams = new URLSearchParams({
      redirect: pathname + search,
    });

    /** This method will jump to the location specified by the redirect parameter */
    const redirect = urlParams.get('redirect');

    // Note: There may be security issues, please note
    if (window.location.pathname !== '/user/login' && !redirect) {
      history.replace({
        pathname: '/user/login',
        search: searchParams.toString(),
      });
    }
  };
  const { styles } = useStyles();

  const { initialState, setInitialState } = useModel('@@initialState');

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      loginOut();
      return;
    }

    history.push(`/account/${key}`);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;

  if (!currentUser || !currentUser.name) {
    return loading;
  }

  const menuItems = [
    ...(menu
      ? [
          {
            key: 'settings',
            icon: <SettingOutlined />,
            label: intl.formatMessage({ id: 'component.avatarDropdown.personalSettings' }),
          },
          {
            type: 'divider' as const,
          },
        ]
      : []),
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: intl.formatMessage({ id: 'component.avatarDropdown.logout' }),
    },
  ];

  return (
    <HeaderDropdown
      menu={{
        selectedKeys: [],
        onClick: onMenuClick,
        items: menuItems,
      }}
    >
      {children}
    </HeaderDropdown>
  );
};
